/**
 * MGI-PILOT-W2-LIFECYCLE-SIBLING-PILOT-PERIMETER-R1 — client-truth mirror of the backend
 * `LIFECYCLE_SIBLING_W2_ROLES` allowlist (`src/routes/incidences.js`).
 *
 * WHAT IT GOVERNS. The four capabilities the `LIFECYCLE-SIBLING` cluster contains and that the
 * delivered client still rendered: generic incidence edit, generic incidence delete, comment edit
 * and comment delete. The signed lifecycle keeps its own affordances untouched — creation, the role
 * inboxes, single read, status transitions, assignment/escalation (a different cluster's business),
 * comment read and comment create.
 *
 * WHY A SHARED CONSTANT AND NOT A ROLE CHECK PER COMPONENT. Four delivered surfaces render these
 * affordances, and a role list copied four times is four things to drift. This is a SURFACE-TRUTH
 * predicate, not an authority: the backend perimeter is the authority, and it now refuses every
 * delivered identity before any business layer. What this file buys is that a delivered user is
 * never shown — nor able to dispatch — a capability the server will refuse.
 *
 * WHY EXACTLY THESE TWO ROLES. It mirrors the merged backend allowlist verbatim: `super_admin` is
 * the vendor, `group_manager` is the CONDITIONAL role this mission is forbidden to adjudicate in or
 * out. Every other role — including the four delivered pilot identities and the out-of-pilot
 * `administration_manager`, which the backend routes already refused before this mission — sees
 * nothing and dispatches nothing.
 */
export const LIFECYCLE_SIBLING_VENDOR_ROLES = Object.freeze([
  'super_admin',
  'group_manager',
] as const)

/** True only for the roles the contained backend routes still admit. */
export function canUseLifecycleSiblingWrites(role: string | null | undefined): boolean {
  return typeof role === 'string' && (LIFECYCLE_SIBLING_VENDOR_ROLES as readonly string[]).includes(role)
}
